import JSZip from 'jszip';
import { PlatformExportFile, PlatformExportFileFormat } from '@/social/types';
import { types as platforms } from '@/social/site';

const zipFileValidators: {[platform: string]: (zip: JSZip) => boolean} = {};
zipFileValidators[platforms.Facebook] = (zip: JSZip) => (
  zip.file('your_facebook_activity/posts/your_posts__check_ins__photos_and_videos_1.json') !== null
  || zip.file('your_facebook_activity/posts/your_uncategorized_photos.json') !== null
  || zip.file('your_facebook_activity/posts/your_videos.json') !== null
  || zip.file('your_facebook_activity/posts/album/0.json') !== null
);
zipFileValidators[platforms.Instagram] = (zip: JSZip) => (
  zip.file('your_instagram_activity/media/posts_1.json') !== null
  || zip.file('your_instagram_activity/media/reels.json') !== null
  || zip.file('your_instagram_activity/media/stories.json') !== null
  || zip.file('your_instagram_activity/media/profile_photos.json') !== null
);

function hasDir(zip: JSZip, dir: string): boolean {
  const entries = Object.keys(zip.files);
  return entries.some(entry => entry.startsWith(dir));
}

function isJson(zip: JSZip): boolean {
  return (zip.file(/.json$/)).length > 0;
}

function isHtml(zip: JSZip): boolean {
  return (zip.file(/.html/)).length > 0;
}

function determineFormat(zip: JSZip): PlatformExportFileFormat|null {
  if (isJson(zip)) return 'json';
  if (isHtml(zip)) return 'html';
  return null;
}

interface PlatformExportFileAnalyzer {
  (zip: JSZip): PlatformExportFile|null
}

class FacebookExportFileAnalyzer {
  static analyze(zip: JSZip): PlatformExportFile|null {
    if (!hasDir(zip, 'your_facebook_activity/')) {
      return null;
    }

    const format = determineFormat(zip);
    if (format === null) return { platform: platforms.Facebook };
    let error;
    if (format !== 'json') {
      error = `
        Your Facebook file is in the wrong format.
        Please repeat the steps to export your Facebook
        posts and ensure you select "JSON" as the format.
      `;
    } else if (!zipFileValidators[platforms.Facebook](zip)) {
      error = 'Your Facebook file does not contain any posts.';
    }

    return {
      platform: platforms.Facebook,
      format,
      error,
    };
  }
}

class InstagramExportFileAnalyzer {
  static analyze(zip: JSZip): PlatformExportFile|null {
    if (!hasDir(zip, 'your_instagram_activity/')) {
      return null;
    }

    const format = determineFormat(zip);
    if (format === null) return { platform: platforms.Instagram };
    let error;
    if (format !== 'json') {
      error = `
        Your Instagram file is in the wrong format.
        Please repeat the steps to export your Facebook posts
        and ensure you select "JSON" as the format.
      `;
    } else if (!zipFileValidators[platforms.Instagram](zip)) {
      error = 'Your Instagram file does not contain any posts.';
    }

    return {
      platform: platforms.Instagram,
      format,
      error,
    };
  }
}

const zipFileAnalyzers: PlatformExportFileAnalyzer[] = [
  FacebookExportFileAnalyzer.analyze,
  InstagramExportFileAnalyzer.analyze,
];

export async function analyzeFile(file: File): Promise<PlatformExportFile|null> {
  const zip = await JSZip.loadAsync(file);
  for (let i = 0; i < zipFileAnalyzers.length; i += 1) {
    const fileInfo = zipFileAnalyzers[i](zip);
    if (fileInfo !== null) return fileInfo;
  }
  return null;
}
